import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Avatar, Button, Dialog, DialogTitle,
    DialogActions, TextField, InputAdornment, IconButton,
    Chip, Divider, Paper, Grid,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import SchoolIcon from '@mui/icons-material/School';
import TranslateIcon from '@mui/icons-material/Translate';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import Navbar from '.';
import { updateDoctorPassword } from '../../service/user';
import { getDoctorById } from '../../service/doctors';

const InfoRow = ({ icon, label, value }) => {
    if (!value) return null;
    return (
        <Box sx={{ display: 'flex', gap: 1.5, py: 1.2, borderBottom: '1px solid #f1f5f9' }}>
            <Box sx={{ color: '#3b82f6', mt: 0.2, flexShrink: 0 }}>{icon}</Box>
            <Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.92rem', color: '#111827', mt: 0.4, lineHeight: 1.6 }}>{value}</Typography>
            </Box>
        </Box>
    );
};

const Profile = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
    const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
    const [errors, setErrors] = useState({ current: '', new: '', confirm: '' });
    const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });

    const handleClickShowPassword = (field) =>
        setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));

    const handlePasswordUpdate = async () => {
        setErrors({ current: '', new: '', confirm: '' });
        if (!passwordData.current) { setErrors(p => ({ ...p, current: 'Current password is required' })); return; }
        if (!passwordData.new) { setErrors(p => ({ ...p, new: 'New password is required' })); return; }
        if (passwordData.new !== passwordData.confirm) { setErrors(p => ({ ...p, confirm: 'Passwords do not match' })); return; }
        if (!user?.id) return;
        try {
            const result = await updateDoctorPassword(user.id, passwordData.current, passwordData.new);
            if (result.error) { setErrors(p => ({ ...p, current: result.error })); }
            else {
                alert(result.message || 'Password updated successfully');
                setPasswordData({ current: '', new: '', confirm: '' });
                setResetPasswordOpen(false);
            }
        } catch { setErrors(p => ({ ...p, current: 'Failed to update password' })); }
    };

    useEffect(() => {
        const fetchUserData = async () => {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const parsedUser = JSON.parse(storedUser);
                try {
                    const doctorResponse = await getDoctorById(parsedUser.id);
                    if (doctorResponse.success && doctorResponse.doctor) {
                        setUser(doctorResponse.doctor);
                        localStorage.setItem('user', JSON.stringify(doctorResponse.doctor));
                    } else { setUser(parsedUser); }
                } catch { setUser(parsedUser); }
            }
        };
        fetchUserData();
    }, []);

    if (!user) return null;

    const avatarSrc = user.id
        ? `/api/v1/doctors/${user.id}/avatar`
        : user.gender?.toLowerCase() === 'male' ? '/man.png'
        : user.gender?.toLowerCase() === 'female' ? '/woman.png'
        : '/user.png';

    const initials = user.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

    const languages = user.languages
        ? user.languages.split(/[,\/]/).map(l => l.trim()).filter(Boolean)
        : [];

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)' }}>
            <Navbar />

            <Box sx={{ px: { xs: 2, sm: 4 }, pb: 5, pt: 3 }}>

                {/* ── Top bar: title + action ── */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#111827' }}>My Profile</Typography>
                        <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', mt: 0.3 }}>View and manage your professional information</Typography>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<LockOutlinedIcon sx={{ fontSize: '0.9rem' }} />}
                        onClick={() => setResetPasswordOpen(true)}
                        sx={{
                            textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: '0.82rem',
                            borderColor: '#3b82f6', color: '#3b82f6', bgcolor: '#fff',
                            '&:hover': { bgcolor: '#eff6ff', borderColor: '#2563eb' },
                        }}
                    >
                        Change Password
                    </Button>
                </Box>

                {/* ── Identity card ── */}
                <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#fff', p: { xs: 2.5, sm: 3 }, mb: 3, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Avatar
                        src={avatarSrc}
                        alt={user.name}
                        imgProps={{ referrerPolicy: "no-referrer" }}
                        sx={{
                            width: { xs: 72, sm: 88 },
                            height: { xs: 72, sm: 88 },
                            boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                            bgcolor: '#dbeafe',
                            fontSize: '1.8rem', fontWeight: 700, color: '#1e3a8a',
                            flexShrink: 0,
                        }}
                    >
                        {initials}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Name + role */}
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', sm: '1.5rem' }, color: '#111827', lineHeight: 1.2 }}>
                            {user.name}
                        </Typography>
                        {user.profession && (
                            <Typography sx={{ color: '#3b82f6', fontWeight: 600, fontSize: '0.92rem', mt: 0.3 }}>
                                {user.profession}
                            </Typography>
                        )}
                        {user.specialization && (
                            <Typography sx={{ color: '#6b7280', fontSize: '0.85rem', mt: 0.2 }}>
                                {user.specialization}
                            </Typography>
                        )}
                        {/* Stat pills */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.2 }}>
                            {user.rating && Number(user.rating) !== 0 && (
                                <Chip
                                    icon={<StarRoundedIcon sx={{ fontSize: '0.9rem !important', color: '#f59e0b !important' }} />}
                                    label={user.rating + "/5" + (user.review_count ? " (" + user.review_count + " reviews)" : "")}
                                    sx={{ bgcolor: '#fffbeb', color: '#92400e', fontWeight: 700, fontSize: '0.8rem', border: '1px solid #fde68a' }}
                                />
                            )}
                            {user.experience && (
                                <Chip label={user.experience} size="small"
                                    sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, fontSize: '0.8rem', border: '1px solid #bfdbfe' }} />
                            )}
                        </Box>
                    </Box>
                </Paper>

                {/* ── Two-column layout ── */}
                <Grid container spacing={3}>

                    {/* LEFT: bio details */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
                            {/* Section header */}
                            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbff' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#111827' }}>Professional Details</Typography>
                            </Box>

                            <Box sx={{ px: 3, py: 1 }}>
                                <InfoRow icon={<SchoolIcon sx={{ fontSize: '1rem' }} />} label="Qualification" value={user.qualification} />
                                <InfoRow icon={<WorkHistoryIcon sx={{ fontSize: '1rem' }} />} label="Experience" value={user.experience} />
                                <InfoRow icon={<InfoOutlinedIcon sx={{ fontSize: '1rem' }} />} label="About" value={user.description} />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* RIGHT: sidebar */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
                            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbff' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#111827' }}>Languages</Typography>
                            </Box>
                            <Box sx={{ px: 3, py: 2 }}>
                                {languages.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {languages.map(lang => (
                                            <Chip key={lang} label={lang}
                                                icon={<TranslateIcon sx={{ fontSize: '0.85rem !important', color: '#15803d !important' }} />}
                                                sx={{ bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 600, fontSize: '0.8rem', border: '1px solid #bbf7d0' }} />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>No languages specified</Typography>
                                )}
                            </Box>
                        </Paper>

                        {/* Quick info card */}
                        {(user.qualification || user.specialization) && (
                            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', mt: 3 }}>
                                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbff' }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#111827' }}>Quick Info</Typography>
                                </Box>
                                <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {user.specialization && (
                                        <Box>
                                            <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Specialization</Typography>
                                            <Typography sx={{ fontSize: '0.88rem', color: '#374151', mt: 0.3 }}>{user.specialization}</Typography>
                                        </Box>
                                    )}
                                    {user.qualification && (
                                        <Box>
                                            <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Qualification</Typography>
                                            <Typography sx={{ fontSize: '0.88rem', color: '#374151', mt: 0.3 }}>{user.qualification}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            </Box>

            {/* Change Password Dialog */}
            <Dialog open={resetPasswordOpen} onClose={() => setResetPasswordOpen(false)} fullWidth maxWidth="xs"
                PaperProps={{ sx: { borderRadius: 3, p: 1, boxShadow: '0 20px 60px rgba(0,0,0,0.12)' } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: '#111827', pb: 0 }}>
                    Change Password
                </DialogTitle>

                <Box sx={{ px: 3, pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[
                        { field: 'current', label: 'Current Password' },
                        { field: 'new', label: 'New Password' },
                        { field: 'confirm', label: 'Confirm Password' },
                    ].map(({ field, label }) => (
                        <TextField key={field} fullWidth label={label}
                            type={showPassword[field] ? 'text' : 'password'}
                            InputLabelProps={{ shrink: true }}
                            value={passwordData[field]}
                            onChange={e => {
                                const val = e.target.value;
                                setPasswordData(p => ({ ...p, [field]: val }));
                                if (field === 'confirm') setErrors(p => ({ ...p, confirm: val !== passwordData.new ? 'Passwords do not match' : '' }));
                            }}
                            error={Boolean(errors[field])}
                            helperText={errors[field]}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => handleClickShowPassword(field)} edge="end">
                                            {showPassword[field] ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    ))}
                </Box>

                <DialogActions sx={{ px: 3, py: 2.5 }}>
                    <Button onClick={() => setResetPasswordOpen(false)}
                        sx={{ textTransform: 'none', color: '#6b7280' }}>Cancel</Button>
                    <Button variant="contained" onClick={handlePasswordUpdate}
                        sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, boxShadow: 'none' }}>
                        Update Password
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Profile;
